/**
 * Smooth Scroll — powered by Lenis (main page only)
 *
 * The panel (.sc-panel-scroll) uses native CSS scroll with inertia.
 * "See Deployments" anchor clicks inside the panel use scrollIntoView.
 */

import Lenis from 'lenis';

(function (): void {
    const LERP = 0.09;

    // ── Main page scroll ─────────────────────────────────────────────────── //

    const pageLenis = new Lenis({
        lerp:        LERP,
        smoothWheel: true,
        // Don't intercept wheel events that originate inside the panel —
        // lets native overflow-y:auto scroll work there.
        prevent: (node: Element): boolean => !!node.closest('#sc-panel'),
    });

    function raf(time: number): void {
        pageLenis.raf(time);
        requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    // ── Panel scroll — native with inertia (CSS handles it) ──────────────── //

    const panel       = document.getElementById('sc-panel');
    const panelScroll = document.querySelector<HTMLElement>('.sc-panel-scroll');

    document.addEventListener('sc:panelopen', function () {
        pageLenis.stop();
        if (panelScroll) panelScroll.scrollTop = 0;
    });

    if (panel) {
        new MutationObserver(function () {
            if (!panel.classList.contains('is-open')) {
                pageLenis.start();
            }
        }).observe(panel, { attributes: true, attributeFilter: ['class'] });
    }

    // ── Scroll-driven body background ────────────────────────────────────── //
    // Features section: fades dark on enter, back to white on exit.
    // Contact section:  fades dark as it scrolls into view (one-way).
    // Both run in a single handler; the darker value wins.

    const featuresSection = document.getElementById('features') as HTMLElement | null;
    const contactSection  = document.getElementById('contact')  as HTMLElement | null;

    pageLenis.on('scroll', function () {
        const vh = window.innerHeight;
        let v = 255; // default white

        if (featuresSection) {
            const r   = featuresSection.getBoundingClientRect();
            const tIn  = Math.max(0, Math.min(1, (vh - r.top)    / vh));
            const tOut = Math.max(0, Math.min(1,  r.bottom        / vh));
            v = Math.min(v, Math.round(255 - 238 * Math.min(tIn, tOut)));
        }

        if (contactSection) {
            const r = contactSection.getBoundingClientRect();
            // Darken quickly (over 40% of vh) and stay dark — no fade out
            const t = Math.max(0, Math.min(1, (vh - r.top) / (vh * 0.75)));
            v = Math.min(v, Math.round(255 - 238 * t));
        }

        document.body.style.transition = 'none';
        document.body.style.backgroundColor = `rgb(${v},${v},${v})`;
    });

    // ── "See Deployments" anchor inside the panel ─────────────────────── //

    document.addEventListener('click', function (e: MouseEvent) {
        const a = (e.target as Element).closest<HTMLAnchorElement>('a[href^="#"]');
        if (!a) return;

        const id     = a.getAttribute('href')?.slice(1);
        if (!id) return;

        const target = document.getElementById(id);
        if (!target || !panel || !panel.contains(target)) return;

        e.preventDefault();

        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
})();
